/* eslint-disable import/no-unresolved */
import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { connect } from "react-redux";
import { push } from "react-router-redux";
import { compose } from "redux";
import { bool, string, shape, arrayOf, func, number } from "prop-types";
import { Field, Form, withFormik } from "formik";
import classNames from "classnames";
import Yup from "yup";
import moment from "moment";
import queryString from "query-string";
import { isNil } from "lodash";
import { startOfDay } from "date-fns";

import PageLoading from "pages/_components/PageLoading";
import Head from "pages/_components/Head";
import Notification from "pages/_components/Notification";
import I18n from "pages/_components/I18n";
import TabletSelectors from "pages/_components/TabletSelectors";
import Container from "pages/_components/Container";
import Button from "pages/_components/Button";
import Image from "pages/_components/Image";
import Col from "react-bootstrap/lib/Col";
import Selector from "pages/_components/fields/formik/Selector";
import { DateField } from "pages/_components/fields/DateField";
import RangeDatePicker from "pages/_components/fields/rangedatepicker/RangeDatePicker";
import { Box, Chip, RoundButton, ScrollerX } from "@ui-kit/components/index.es";

import {
    isEnabledApi5,
    isEnabledMvp,
    isEnabledFundPerformance,
    hasFundPositions,
    resolveFundcorpFlowGoBackPath,
    toSafeMoment,
} from "util/fundcorp";

import { actions as fundcorpActions, selectors as fundcorpSelectors } from "reducers/fundcorp";
import { selectors as sessionSelectors } from "reducers/session";
import { selectors as statusSelectors } from "reducers/status";

import * as i18nUtils from "util/i18n";
import * as configUtils from "util/config";
import { evalEqualsValues } from "util/form";
import { getPostLoginHomePath } from "util/v3UxConfig";
import { useEmbeddedV3ShellLayoutSync } from "util/v3ShellLayoutSync";
import { resizableRoute } from "pages/_components/Resizable";
import OperationRightContent from "./_components/OperationRightContent";
import FundcorpList from "./_components/FundcorpList";
import FundcorpRightContent from "./_components/FundcorpRightContent";
import FundPerformanceTab from "./_components/FundPerformanceTab";

const FORM_ID = "fundcorp.list";
function FundcorpOperationsMain(props) {
    const {
        dispatch,
        fetching,
        isDesktop,
        isSmallDesktop,
        fetchingDownload,
        isFetchingFundcorp,
        optionSelected,
        positions,
        movements,
        requests,
        values,
        setFieldValue,
        funds,
        shareAccounts,
        positionsByShareAccount,
        filtersData,
        location,
        submitForm,
        activeEnvironment,
        embeddedInV3Shell,
        lastPathname,
    } = props;

    const {
        positionsType,
        positionDate,
        movementsDateFrom,
        movementsDateTo,
        movementsShareAccount,
        requestsShareAccount,
        requestsDateFrom,
        requestsDateTo,
    } = values;

    const [showForm, setShowForm] = useState(false);
    const [firstEffect, setFirstEffect] = useState(false);
    const [performancePreload, setPerformancePreload] = useState(null);
    const [isPreHydrated, setIsPreHydrated] = useState(false);
    const enableRangeDatefield = configUtils.getBoolean("frontend.show.RangeDatePicker.functionalities", false);
    const enableNewSubmitButton = configUtils.getBoolean("frontend.Show.FilterButton.Functionalities", false);
    const showMonthlyReports = configUtils.getBoolean("frontend.show.fundExtracts.functionalities", false);
    const enableFundsImprovements = configUtils.getBoolean("frontend.show.FundImprovements.functionalities", false);

    const useAPI5 = isEnabledApi5();
    const possibleOptions = useAPI5 ? ["positions", "movementsAndRequests"] : ["positions", "movements", "requests"];
    if (isEnabledFundPerformance()) {
        possibleOptions.push("performance");
    }
    const fundHasPositions = hasFundPositions(positionsByShareAccount);
    const options = possibleOptions.map((possibleOption) => ({
        value: possibleOption,
        label: `fundcorp.${possibleOption}.label`,
        disabled: possibleOption === "performance" && !fundHasPositions,
    }));

    useLayoutEffect(() => {
        setIsPreHydrated(false);
        const { query } = queryString.parseUrl(location.search);
        dispatch(fundcorpActions.listFundcorpPre("operations", query?.opt));
        if (query?.opt === "performance") {
            const { c, f, a, vc } = query;
            setPerformancePreload({
                shareAccountId: c ? parseInt(c, 10) : undefined,
                fundId: f,
                abreviatura: a,
                verifyCode: vc,
            });
        } else if (query?.opt) {
            const { c, f } = query;

            setFirstEffect(true);
            setFieldValue("movementsFund", parseInt(f, 10));
            setFieldValue("movementsShareAccount", parseInt(c, 10));
        } else {
            setFieldValue("movementsFund", "0");
        }
    }, [dispatch, location, setFieldValue]);

    useEffect(() => {
        if (!fetching) {
            setIsPreHydrated(true);
        }
    }, [fetching]);

    const isBootstrapping = !isPreHydrated || fetching;

    useEffect(() => {
        if (!requestsShareAccount && shareAccounts && shareAccounts.length > 0) {
            setFieldValue("requestsShareAccount", shareAccounts[0].id);
        }
        if (!movementsShareAccount && shareAccounts && shareAccounts.length > 0) {
            setFieldValue("movementsShareAccount", shareAccounts[0].id);
        }
        if (shareAccounts && shareAccounts.length > 0 && firstEffect) {
            submitForm();
            setFirstEffect(false);
        }
    }, [
        requestsShareAccount,
        movementsShareAccount,
        firstEffect,
        setFirstEffect,
        shareAccounts,
        setFieldValue,
        submitForm,
    ]);

    const resetDefaultValues = () => {
        setFieldValue("positionsType", "byShare");
        setFieldValue("positionDate", moment(new Date()));
        const selectedFavoriteAccount = shareAccounts?.find((sAccount) => sAccount?.isFavorite);
        const idAccountDefault = location?.state?.idAccount || selectedFavoriteAccount?.id;
        if (!values?.movementsShareAccount && idAccountDefault && shareAccounts?.length > 0) {
            setFieldValue("movementsShareAccount", idAccountDefault);
        }
        setFieldValue("requestsShareAccount", shareAccounts && shareAccounts.length > 0 ? shareAccounts[0].id : null);
        setFieldValue("movementsFund", "0");
        if (enableRangeDatefield) {
            setFieldValue("movementsDates", [
                moment()
                    .subtract(1, "weeks")
                    .toDate(),
                new Date(),
            ]);
        } else {
            setFieldValue("movementsDateFrom", moment().subtract(1, "weeks"));
            setFieldValue("movementsDateTo", moment(new Date()));
        }
        setFieldValue("requestsDateFrom", moment().subtract(1, "weeks"));
        setFieldValue("requestsDateTo", moment(new Date()));
    };

    useEffect(() => {
        if (isBootstrapping) {
            return;
        }
        resetDefaultValues();
        // eslint-disable-next-line
    }, [location, isBootstrapping]);

    const handleExport = useCallback(
        (format) => {
            const summary = { positions, movements, requests, optionSelected, filtersData };
            dispatch(fundcorpActions.downloadFundcorpList(summary, format));
        },
        [positions, movements, requests, optionSelected, filtersData, dispatch],
    );

    const hasPositionsToday = useCallback(() => {
        const positionDateFilter = filtersData?.positionDate;
        const today = new Date();

        if (!positionDateFilter || !positionsByShareAccount) {
            return false;
        }
        if (!(positionDateFilter instanceof Date)) {
            return positionsByShareAccount.length > 0 && positionDateFilter.toDate().getDate() === today.getDate();
        }
        return positionsByShareAccount.length > 0 && positionDateFilter.getDate() === today.getDate();
    }, [filtersData, positionsByShareAccount]);

    const hideDownload = optionSelected === "performance";

    const fundcorpShellRightContentMobile = useMemo(() => {
        if (!embeddedInV3Shell || isDesktop || fetching || isNil(shareAccounts)) {
            return null;
        }
        return showMonthlyReports ? (
            <FundcorpRightContent
                activeEnvironment={activeEnvironment}
                isDesktop={isDesktop}
                dispatch={dispatch}
                isSmallDesktop={isSmallDesktop}
                shareAccounts={shareAccounts}
                hasPositionsToday={hasPositionsToday()}
                handleExport={handleExport}
                isFetchingDownload={fetchingDownload}
                embeddedInV3Shell={embeddedInV3Shell}
                hideDownload={hideDownload}
            />
        ) : (
            <OperationRightContent
                activeEnvironment={activeEnvironment}
                isDesktop={isDesktop}
                dispatch={dispatch}
                isSmallDesktop={isSmallDesktop}
                shareAccounts={shareAccounts}
                hasPositionsToday={hasPositionsToday()}
                handleExport={handleExport}
                isFetchingDownload={fetchingDownload}
                hideDownload={hideDownload}
            />
        );
    }, [
        embeddedInV3Shell,
        isDesktop,
        fetching,
        shareAccounts,
        showMonthlyReports,
        activeEnvironment,
        isSmallDesktop,
        fetchingDownload,
        hasPositionsToday,
        handleExport,
        hideDownload,
    ]);

    const resolvedGoBackPath = useMemo(() => {
        const returnFromState = location?.state?.return;
        if (returnFromState && returnFromState !== "/") {
            return returnFromState;
        }
        const fromLast = resolveFundcorpFlowGoBackPath({
            returnFromState: undefined,
            lastHrefPathname: lastPathname,
            currentPathname: location?.pathname || "",
        });
        if (fromLast === "/fundcorpOperations" && (location?.pathname || "") === "/fundcorpOperations") {
            return getPostLoginHomePath(true);
        }
        return fromLast;
    }, [location, lastPathname]);

    useEmbeddedV3ShellLayoutSync(dispatch, {
        embeddedInV3Shell,
        isDesktop,
        syncDesktopShellHeader: true,
        titleKey: "menu.invesments.tradeFunds",
        goBackPath: resolvedGoBackPath,
        scopes: [FORM_ID],
        rightContentMobile: fundcorpShellRightContentMobile,
    });

    const handleBack = () => {
        if (!embeddedInV3Shell) {
            const back = location?.state?.return;
            dispatch(push(back && back !== "/" ? back : "/desktop"));
            return;
        }
        dispatch(push(resolvedGoBackPath));
    };

    const changeOption = (option) => {
        if (option === "performance" && !fundHasPositions) {
            return;
        }
        const hasShareAccounts = shareAccounts && shareAccounts.length > 0;
        const defaultShareAccount = hasShareAccounts ? shareAccounts[0].id : null;
        dispatch(fundcorpActions.saveOptionSelected(option));
        setFieldValue("positionsType", "byShare");
        setFieldValue("positionDate", moment(new Date()));
        setFieldValue("movementsShareAccount", defaultShareAccount);
        setFieldValue("requestsShareAccount", defaultShareAccount);
        setFieldValue("movementsFund", "0");
        if (enableRangeDatefield) {
            setFieldValue("movementsDates", [
                moment()
                    .subtract(1, "weeks")
                    .toDate(),
                new Date(),
            ]);
        } else {
            setFieldValue("movementsDateFrom", moment().subtract(1, "weeks"));
            setFieldValue("movementsDateTo", moment(new Date()));
        }
        setFieldValue("requestsDateFrom", moment().subtract(1, "weeks"));
        setFieldValue("requestsDateTo", moment(new Date()));
        resetDefaultValues();
        if (hasShareAccounts && option !== "performance") {
            submitForm();
        }
    };

    const rowButtons = () => (
        <TabletSelectors
            isDesktop={isDesktop}
            possibleOptions={options}
            changeOption={changeOption}
            optionSelected={optionSelected}
            imageClassName="ml-3"
            transparentBackground={optionSelected === "performance"}
            containerClass={classNames(
                "fundcorp-tablet-selector py-25",
                { "mt-4 mb-2": isDesktop },
                { "my-0": !isDesktop },
            )}
        />
    );

    const fetchNextPage = (page) => {
        const movementsShareAccountDescription = movementsShareAccount
            ? shareAccounts.find(({ id }) => id === movementsShareAccount)?.description
            : null;
        const movementsFund = filtersData && filtersData.movementsFund ? filtersData.movementsFund : "0";
        const fundOptions = [
            {
                id: "0",
                description: i18nUtils.get(`${FORM_ID}.fund.options.all`),
            },
            ...funds,
        ];
        const movementsFundDescription = movementsFund
            ? fundOptions.find(({ id }) => id === movementsFund)?.description
            : null;
        const defaultWeekAgo = moment()
            .subtract(1, "weeks")
            .toDate();
        const defaultToday = new Date();
        const movementsDates = [
            toSafeMoment(filtersData?.movementsDateFrom, defaultWeekAgo),
            toSafeMoment(filtersData?.movementsDateTo, defaultToday),
        ];
        const requestsShareAccountDescription = requestsShareAccount
            ? shareAccounts.find(({ id }) => id === requestsShareAccount).description
            : null;
        const verifyCode =
            movementsShareAccount || requestsShareAccount
                ? shareAccounts.find(
                      ({ id }) =>
                          id ===
                          (optionSelected === "movements" || optionSelected === "movementsAndRequests"
                              ? movementsShareAccount
                              : requestsShareAccount),
                  ).verifyCode
                : null;

        dispatch(
            fundcorpActions.listFundcorp(
                optionSelected,
                positionsType,
                positionDate,
                movementsShareAccount,
                movementsShareAccountDescription,
                movementsFund,
                movementsFundDescription,
                enableRangeDatefield ? movementsDates[0] : movementsDateFrom,
                enableRangeDatefield ? movementsDates[1] : movementsDateTo,
                requestsShareAccount,
                requestsShareAccountDescription,
                requestsDateFrom,
                requestsDateTo,
                verifyCode,
                page,
            ),
        );
    };

    if (isNil(shareAccounts) || isBootstrapping) {
        return (
            <>
                {!embeddedInV3Shell && <Notification scopeToShow={FORM_ID} />}
                <PageLoading loading className={isDesktop ? "screen-loader min-height-vh-page" : undefined} />
            </>
        );
    }

    const positionsTypeOptions = [
        {
            id: "byShare",
            label: i18nUtils.get(`${FORM_ID}.positionsType.byShare`),
        },
        {
            id: "byFund",
            label: i18nUtils.get(`${FORM_ID}.positionsType.byFund`),
        },
    ];

    const shareAccountOptions = [
        ...shareAccounts
            .sort((a, b) => {
                if (a.description > b.description) {
                    return 1;
                }
                if (a.description < b.description) {
                    return -1;
                }
                return 0;
            })
            .map((share) => ({
                value: share.id,
                label: `${share.id} ${share.description}`,
            })),
    ];

    const fundOptions = [
        {
            value: "0",
            label: i18nUtils.get(`${FORM_ID}.fund.options.all`),
        },
        ...funds.map((fund) => ({
            value: fund.id,
            label: fund.description,
        })),
    ];

    const centerElement = () => (
        <div className="d-flex align-items-center">
            <I18n
                id="fundcorp.title.operations.secondPart"
                component="h1"
                componentProps={{ className: "my-0 no-wrap" }}
            />
            <h1 className="p-25 my-0">|</h1>
            <Image src="images/fundcorp-logo.svg" wrapperClassName="fundcorp-logo" />
        </div>
    );

    const centerElementMobile = () => (
        <div className="d-flex flex-column align-items-center">
            <I18n id="fundcorp.title.operations.secondPart" component="h1" componentProps={{ className: "my-0" }} />
        </div>
    );

    const getReturnPath = () => (location ? `${location.pathname}${location.search || ""}` : undefined);

    const handleSubscribe = (url) => {
        const showMvp4Release = isEnabledMvp("MVP4");
        const returnPath = getReturnPath();
        if (showMvp4Release) {
            dispatch(fundcorpActions.getInvestorProfile(url, returnPath));
        } else {
            dispatch(push({ pathname: url, state: returnPath ? { return: returnPath } : undefined }));
        }
    };

    const handleRescue = (url) => {
        const returnPath = getReturnPath();
        dispatch(push({ pathname: url, state: returnPath ? { return: returnPath } : undefined }));
    };

    return (
        <>
            {!embeddedInV3Shell && <Notification scopeToShow={FORM_ID} />}
            <div className="admin-detail-head px-0 fundcorp__mobile-main">
                {isDesktop ? <Head onBack={handleBack} /> : undefined}
                {!(embeddedInV3Shell && !isDesktop) && (
                    <Head
                        centerElement={isDesktop ? centerElement : centerElementMobile}
                        accessibilityTextId="fundcorp.title.operations.secondPart"
                        rightContent={() => {
                            if (!fetching) {
                                return showMonthlyReports ? (
                                    <FundcorpRightContent
                                        activeEnvironment={activeEnvironment}
                                        isDesktop={isDesktop}
                                        dispatch={dispatch}
                                        isSmallDesktop={isSmallDesktop}
                                        shareAccounts={shareAccounts}
                                        hasPositionsToday={hasPositionsToday()}
                                        handleExport={handleExport}
                                        isFetchingDownload={fetchingDownload}
                                        embeddedInV3Shell={embeddedInV3Shell}
                                        hideDownload={hideDownload}
                                    />
                                ) : (
                                    <OperationRightContent
                                        activeEnvironment={activeEnvironment}
                                        isDesktop={isDesktop}
                                        dispatch={dispatch}
                                        isSmallDesktop={isSmallDesktop}
                                        shareAccounts={shareAccounts}
                                        hasPositionsToday={hasPositionsToday()}
                                        handleExport={handleExport}
                                        isFetchingDownload={fetchingDownload}
                                        hideDownload={hideDownload}
                                    />
                                );
                            }
                            return undefined;
                        }}
                        imageStyle="mr-2"
                        navbarClassName={!isDesktop ? "fundcorp__navbar-header" : undefined}
                        headerClassName={!isDesktop ? "blue-main-header-mobile" : undefined}
                        onBack={!isDesktop && handleBack}
                    />
                )}
                <PageLoading
                    loading={fetching}
                    className={
                        isDesktop
                            ? "screen-loader min-height-vh-page"
                            : classNames({ "container-white": optionSelected !== "performance" })
                    }>
                    {enableFundsImprovements && !isDesktop ? (
                        <Box w="100%" css={{ maxWidth: "100%", minWidth: 0, overflowX: "hidden" }}>
                            <Box justify="center" py={12} gap={16} w="100%">
                                <RoundButton
                                    aria-label={i18nUtils.get("fund.operation.subscribe")}
                                    text={i18nUtils.get("fund.operation.subscribe")}
                                    variant="outline"
                                    icon="StocksUp"
                                    iconVariant="outline"
                                    size="large"
                                    onClick={() => handleSubscribe("/fundcorpOperations/subscribe")}
                                    isInactive={!activeEnvironment.permissions.fundSubscribe}
                                />
                                <RoundButton
                                    aria-label={i18nUtils.get("fund.operation.rescue")}
                                    text={i18nUtils.get("fund.operation.rescue")}
                                    variant="outline"
                                    icon="RescueDolar"
                                    iconVariant="outline"
                                    size="large"
                                    onClick={() => handleRescue("/fundcorpOperations/rescue")}
                                    isInactive={!(activeEnvironment.permissions.fundRescue && hasPositionsToday())}
                                />
                                <RoundButton
                                    aria-label={i18nUtils.get("fund.operation.fundExtracts")}
                                    text={i18nUtils.get("fund.operation.fundExtracts")}
                                    variant="outline"
                                    icon="Memo"
                                    iconVariant="outline"
                                    size="large"
                                    onClick={() => dispatch(fundcorpActions.setReportsDrawerVisible(true))}
                                    isInactive={!shareAccounts?.length}
                                />
                            </Box>
                            <Box w="100%" pl={16} css={{ maxWidth: "100%", minWidth: 0 }}>
                                <ScrollerX>
                                    {options.map(({ value, label, disabled }) => (
                                        <Box key={value} css={{ flexShrink: 0 }}>
                                            <Chip
                                                size="small"
                                                label={i18nUtils.get(label)}
                                                pressed={optionSelected === value}
                                                disabled={disabled}
                                                onClick={() => changeOption(value)}
                                            />
                                        </Box>
                                    ))}
                                </ScrollerX>
                            </Box>
                        </Box>
                    ) : (
                        rowButtons()
                    )}
                    {!isDesktop && optionSelected !== "performance" && (
                        <div className="justify-content-end d-flex">
                            <Button
                                className="btn-link px-2 my-0 mr-2"
                                block={false}
                                label={showForm ? "echeq.button.hideFilter" : "echeq.button.seeFilter"}
                                onClick={() => setShowForm(!showForm)}
                            />
                        </div>
                    )}
                    {(isDesktop || showForm || optionSelected === "performance") && (
                        <Form autoComplete="off" className={enableRangeDatefield ? "fundcorp__form_v2" : undefined}>
                            <Container
                                className={classNames(
                                    "align-items-left account-header-align-items-left fundcorp-form account-header-detail",
                                    { "backgroud-transparent": optionSelected === "performance" },
                                )}
                                gridClassName="form-content"
                                rowClassName={classNames("justify-content-left px-2", {
                                    "pb-4": optionSelected !== "positions",
                                    "pb-15": optionSelected === "positions",
                                })}>
                                {optionSelected === "positions" && (
                                    <>
                                        <Col
                                            md={4}
                                            lg={3}
                                            className={`d-flex align-items-center ${
                                                isDesktop ? "p-2 mt-3 ml-2 mr-4" : "mt-3 px-2 space-between"
                                            }`}>
                                            <Field
                                                component={Selector}
                                                formGroupClassName="mb-0 text-uppercase"
                                                options={positionsTypeOptions}
                                                idForm={FORM_ID}
                                                idField="positionsType"
                                                name="positionsType"
                                                inLineControl
                                                isRequired
                                                renderAs="radio"
                                                hideLabel
                                                spaceBetween={!isDesktop}
                                                defaultValue={positionsType}
                                                onCustomChange={() => {
                                                    dispatch(fundcorpActions.setChangeDataFormat());
                                                    submitForm();
                                                }}
                                                customOnChange
                                            />
                                        </Col>
                                        <Col
                                            xs={12}
                                            md={4}
                                            lg={2}
                                            className={classNames("p-2", {
                                                "w-100": !isDesktop,
                                                "px-3 mb-35 d-flex align-items-end": isDesktop,
                                            })}>
                                            <Field
                                                idField="positionDate"
                                                component={DateField}
                                                formGroupClassName="w-100 mb-0"
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                name="positionDate"
                                                minDate={null}
                                                maxDate={startOfDay(moment())}
                                                value={positionDate}
                                            />
                                        </Col>
                                    </>
                                )}

                                {(optionSelected === "movements" || optionSelected === "movementsAndRequests") && (
                                    <>
                                        <Col
                                            xs={12}
                                            md={6}
                                            lg={3}
                                            className={classNames("p-2", {
                                                "d-flex align-items-end min-width-25 max-width-25":
                                                    isDesktop && !enableRangeDatefield,
                                            })}>
                                            <Field
                                                component={Selector}
                                                formGroupClassName="mb-0 w-100"
                                                options={shareAccountOptions}
                                                idForm={FORM_ID}
                                                idField="movementsShareAccount"
                                                name="movementsShareAccount"
                                                isRequired
                                                placeholder={i18nUtils.get(`${FORM_ID}.shareAccount.placeholder`)}
                                                value={movementsShareAccount}
                                            />
                                        </Col>
                                        <Col
                                            xs={12}
                                            md={6}
                                            lg={3}
                                            className={classNames("p-2", {
                                                "d-flex align-items-end min-width-25 max-width-25":
                                                    isDesktop && !enableRangeDatefield,
                                            })}>
                                            <Field
                                                component={Selector}
                                                formGroupClassName="mb-0 w-100"
                                                options={fundOptions}
                                                idForm={FORM_ID}
                                                idField="movementsFund"
                                                name="movementsFund"
                                                isRequired
                                                searchable={isDesktop}
                                            />
                                        </Col>
                                        {enableRangeDatefield ? (
                                            <Col
                                                xs={12}
                                                md={6}
                                                lg={4}
                                                className={classNames("p-2", { "d-flex align-items-end": isDesktop })}>
                                                <Field
                                                    component={RangeDatePicker}
                                                    formGroupClassName="mb-0"
                                                    idForm={FORM_ID}
                                                    name="movementsDates"
                                                    maxDate={new Date()}
                                                    required
                                                />
                                            </Col>
                                        ) : (
                                            <>
                                                <Col
                                                    className={classNames("p-2", {
                                                        "w-50": !isDesktop,
                                                        "d-flex align-items-end": isDesktop,
                                                    })}>
                                                    <Field
                                                        idField="movementsDateFrom"
                                                        component={DateField}
                                                        formGroupClassName="mb-0"
                                                        hidePlaceholder
                                                        idForm={FORM_ID}
                                                        name="movementsDateFrom"
                                                        minDate={null}
                                                        maxDate={movementsDateTo}
                                                        value={movementsDateFrom}
                                                    />
                                                </Col>
                                                <Col
                                                    className={classNames("p-2", {
                                                        "w-50": !isDesktop,
                                                        "d-flex align-items-end": isDesktop,
                                                    })}>
                                                    <Field
                                                        idField="movementsDateTo"
                                                        component={DateField}
                                                        formGroupClassName="mb-0"
                                                        hidePlaceholder
                                                        idForm={FORM_ID}
                                                        name="movementsDateTo"
                                                        minDate={movementsDateFrom}
                                                        maxDate={moment()}
                                                        style={{ alignSelf: "flex-end" }}
                                                        value={movementsDateTo}
                                                    />
                                                </Col>
                                            </>
                                        )}
                                    </>
                                )}

                                {optionSelected === "performance" && (
                                    <Col xs={12} className="p-2" style={{ minWidth: 0, maxWidth: "100%" }}>
                                        <FundPerformanceTab preload={performancePreload} />
                                    </Col>
                                )}
                                {optionSelected === "requests" && (
                                    <>
                                        <Col
                                            className={classNames("p-2", {
                                                "w-100": !isDesktop,
                                                "d-flex align-items-end": isDesktop,
                                            })}>
                                            <Field
                                                component={Selector}
                                                formGroupClassName="mb-0"
                                                options={shareAccountOptions}
                                                idForm={FORM_ID}
                                                idField="requestsShareAccount"
                                                name="requestsShareAccount"
                                                isRequired
                                                placeholder={i18nUtils.get(`${FORM_ID}.shareAccount.placeholder`)}
                                                value={requestsShareAccount}
                                            />
                                        </Col>
                                        <Col
                                            className={classNames("p-2", {
                                                "w-50": !isDesktop,
                                                "d-flex align-items-end": isDesktop,
                                            })}>
                                            <Field
                                                idField="requestsDateFrom"
                                                component={DateField}
                                                formGroupClassName="mb-0"
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                name="requestsDateFrom"
                                                minDate={null}
                                                maxDate={requestsDateTo}
                                                value={requestsDateFrom}
                                            />
                                        </Col>
                                        <Col
                                            className={classNames("p-2", {
                                                "w-50": !isDesktop,
                                                "d-flex align-items-end": isDesktop,
                                            })}>
                                            <Field
                                                idField="requestsDateTo"
                                                component={DateField}
                                                formGroupClassName="mb-0"
                                                hidePlaceholder
                                                idForm={FORM_ID}
                                                name="requestsDateTo"
                                                minDate={requestsDateFrom}
                                                style={{ alignSelf: "flex-end" }}
                                                value={requestsDateTo}
                                            />
                                        </Col>
                                    </>
                                )}
                                {optionSelected !== "performance" && (
                                    <Col
                                        xs={12}
                                        md={6}
                                        lg={2}
                                        className={classNames("d-flex align-items-end p-2 px-0", {
                                            "mb-35": optionSelected === "positions",
                                        })}>
                                        <Button
                                            bsStyle="primary"
                                            label={
                                                enableNewSubmitButton ? "global.filter" : `${FORM_ID}.btn.filter.label`
                                            }
                                            loading={isFetchingFundcorp}
                                            type="submit"
                                            className={classNames({
                                                "filter-button w-80 mb-0": isDesktop,
                                                "mt-4": !isDesktop,
                                                "w-100": enableRangeDatefield,
                                                "text-transform-none": enableNewSubmitButton,
                                            })}
                                            image={enableNewSubmitButton ? "filter-icon.svg" : undefined}
                                            imageStyle="mr-2"
                                            disabled={
                                                enableNewSubmitButton &&
                                                evalEqualsValues(
                                                    filtersData || {},
                                                    values,
                                                    [
                                                        "positionsType",
                                                        "page",
                                                        ...(optionSelected !== "positions" ? ["positionsType"] : []),
                                                        ...(optionSelected !== "positions" ? ["positionDate"] : []),
                                                        ...(optionSelected !== "movements" &&
                                                        optionSelected !== "movementsAndRequests"
                                                            ? ["movementsShareAccount", "movementsFund"]
                                                            : []),
                                                        ...((optionSelected !== "movements" &&
                                                            optionSelected !== "movementsAndRequests") ||
                                                        !enableRangeDatefield
                                                            ? ["movementsDates"]
                                                            : []),
                                                        ...((optionSelected !== "movements" &&
                                                            optionSelected !== "movementsAndRequests") ||
                                                        enableRangeDatefield
                                                            ? ["movementsDateFrom", "movementsDateTo"]
                                                            : []),
                                                        ...(optionSelected !== "requests"
                                                            ? [
                                                                  "requestsShareAccount",
                                                                  "requestsDateFrom",
                                                                  "requestsDateTo",
                                                              ]
                                                            : []),
                                                    ],
                                                    [
                                                        ...(optionSelected === "positions" ? ["positionDate"] : []),
                                                        ...((optionSelected === "movements" ||
                                                            optionSelected === "movementsAndRequests") &&
                                                        enableRangeDatefield
                                                            ? ["movementsDates"]
                                                            : []),
                                                        ...((optionSelected === "movements" ||
                                                            optionSelected === "movementsAndRequests") &&
                                                        !enableRangeDatefield
                                                            ? ["movementsDateFrom", "movementsDateTo"]
                                                            : []),
                                                    ],
                                                )
                                            }
                                        />
                                    </Col>
                                )}
                            </Container>
                        </Form>
                    )}
                    <FundcorpList {...props} hasPositionsToday={hasPositionsToday} fetchNextPage={fetchNextPage} />
                </PageLoading>
            </div>
        </>
    );
}

FundcorpOperationsMain.propTypes = {
    dispatch: func.isRequired,
    fetching: bool.isRequired,
    isDesktop: bool.isRequired,
    isSmallDesktop: bool.isRequired,
    fetchingDownload: bool.isRequired,
    isFetchingFundcorp: bool.isRequired,
    optionSelected: string.isRequired,
    positions: arrayOf(shape({})).isRequired,
    movements: arrayOf(shape({})).isRequired,
    requests: arrayOf(shape({})).isRequired,
    values: shape({}).isRequired,
    setFieldValue: func.isRequired,
    funds: arrayOf(shape({})).isRequired,
    shareAccounts: arrayOf(shape({})).isRequired,
    positionsByShareAccount: arrayOf(shape({})).isRequired,
    pendingFundRequests: arrayOf(shape({})).isRequired,
    filtersData: shape({}).isRequired,
    setValues: func.isRequired,
    location: shape({ search: string }).isRequired,
    submitForm: func.isRequired,
    activeEnvironment: shape({
        permissions: shape({
            fundRescue: bool,
            fundSubscribe: bool,
        }),
    }).isRequired,
    totalPages: number.isRequired,
    page: number.isRequired,
    lastPathname: string.isRequired,
    embeddedInV3Shell: bool,
};

FundcorpOperationsMain.defaultProps = {
    embeddedInV3Shell: false,
};

const mapStateToProps = (state) => ({
    fetching: fundcorpSelectors.getFetching(state),
    fetchingDownload: fundcorpSelectors.getFetchingDownload(state),
    optionSelected: fundcorpSelectors.getOptionSelected(state),
    isFetchingFundcorp: fundcorpSelectors.isFetchingFundcorp(state),
    funds: fundcorpSelectors.getFunds(state),
    shareAccounts: fundcorpSelectors.getShareAccounts(state),
    positions: fundcorpSelectors.getPositions(state),
    positionsByShareAccount: fundcorpSelectors.getPositionsByShareAccount(state),
    pendingFundRequests: fundcorpSelectors.getPendingFundRequests(state),
    movements: fundcorpSelectors.getMovements(state),
    page: fundcorpSelectors.getPage(state),
    totalPages: fundcorpSelectors.getTotalPages(state),
    requests: fundcorpSelectors.getRequests(state),
    filtersData: fundcorpSelectors.getFiltersData(state),
    activeEnvironment: sessionSelectors.getActiveEnvironment(state),
    fundFeatures: fundcorpSelectors.getFundFeatures(state),
    changeDataFormat: fundcorpSelectors.getChangeDataFormat(state),
    lastPathname: statusSelectors.getPathname(state) || "",
});

export default compose(
    connect(mapStateToProps),
    resizableRoute,
    withFormik({
        enableReinitialize: true,
        validateOnChange: false,
        validateOnBlur: false,
        mapPropsToValues: (props) => {
            const { filtersData, shareAccounts, page } = props;
            const defaultShareAccount = shareAccounts && shareAccounts.length > 0 ? shareAccounts[0].id : null;
            const defaultWeekAgo = moment().subtract(1, "weeks");
            const defaultToday = moment();
            const movementsShareAccount =
                filtersData && filtersData.movementsShareAccount
                    ? filtersData.movementsShareAccount
                    : defaultShareAccount;
            const requestsShareAccount =
                filtersData && filtersData.requestsShareAccount
                    ? filtersData.requestsShareAccount
                    : defaultShareAccount;
            const movementsDateFrom = toSafeMoment(filtersData?.movementsDateFrom, defaultWeekAgo);
            const movementsDateTo = toSafeMoment(filtersData?.movementsDateTo, defaultToday);
            const requestsDateFrom = toSafeMoment(filtersData?.requestsDateFrom, defaultWeekAgo);
            const requestsDateTo = toSafeMoment(filtersData?.requestsDateTo, defaultToday);
            const positionDate = toSafeMoment(filtersData?.positionDate, defaultToday);
            return {
                positionsType: filtersData && filtersData.positionsType ? filtersData.positionsType : "byShare",
                positionDate,

                movementsShareAccount,
                movementsFund: filtersData && filtersData.movementsFund ? filtersData.movementsFund : "0",
                movementsDateFrom,
                movementsDateTo,
                movementsDates: [movementsDateFrom.toDate(), movementsDateTo.toDate()],
                requestsShareAccount,
                requestsDateFrom,
                requestsDateTo,
                page,
            };
        },
        validationSchema: (props) =>
            Yup.lazy(() => {
                const { optionSelected } = props;
                const enableRangeDatefield = configUtils.getBoolean(
                    "frontend.show.RangeDatePicker.functionalities",
                    false,
                );
                let validationObject = {};

                if (optionSelected === "positions") {
                    validationObject = {
                        ...validationObject,
                        positionDate: Yup.string()
                            .nullable()
                            .required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                    };
                }
                if (optionSelected === "movements" || optionSelected === "movementsAndRequests") {
                    validationObject = {
                        ...validationObject,
                        ...(enableRangeDatefield
                            ? {
                                  movementsDates: Yup.mixed().test(
                                      "dates",
                                      i18nUtils.get(`${FORM_ID}.validation.range.date`),
                                      ([from, to]) => from && to,
                                  ),
                              }
                            : {
                                  movementsDateFrom: Yup.string()
                                      .nullable()
                                      .required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                                  movementsDateTo: Yup.string()
                                      .nullable()
                                      .required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                              }),
                    };
                }
                if (optionSelected === "requests") {
                    validationObject = {
                        ...validationObject,
                        requestsDateFrom: Yup.string()
                            .nullable()
                            .required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                        requestsDateTo: Yup.string()
                            .nullable()
                            .required(i18nUtils.get(`${FORM_ID}.field.error.required`)),
                    };
                }
                return Yup.object().shape(validationObject);
            }),
        handleSubmit: (
            {
                positionsType,
                positionDate,
                movementsShareAccount,
                movementsFund,
                movementsDateFrom,
                movementsDateTo,
                movementsDates,
                requestsShareAccount,
                requestsDateFrom,
                requestsDateTo,
                page,
            },
            formikBag,
        ) => {
            const { dispatch, optionSelected, funds, shareAccounts } = formikBag.props;
            const enableRangeDatefield = configUtils.getBoolean("frontend.show.RangeDatePicker.functionalities", false);

            const fundOptions = [
                {
                    id: "0",
                    description: i18nUtils.get(`${FORM_ID}.fund.options.all`),
                },
                ...funds,
            ];
            const movementsShareAccountDescription = movementsShareAccount
                ? shareAccounts.find(({ id }) => id === movementsShareAccount)?.description
                : null;
            const movementsFundDescription = movementsFund
                ? fundOptions.find(({ id }) => id === movementsFund).description
                : null;
            const requestsShareAccountDescription = requestsShareAccount
                ? shareAccounts.find(({ id }) => id === requestsShareAccount).description
                : null;
            const verifyCode =
                movementsShareAccount || requestsShareAccount
                    ? shareAccounts?.find(
                          ({ id }) =>
                              id ===
                              (optionSelected === "movements" || optionSelected === "movementsAndRequests"
                                  ? movementsShareAccount
                                  : requestsShareAccount),
                      ).verifyCode
                    : null;

            dispatch(
                fundcorpActions.listFundcorp(
                    optionSelected,
                    positionsType,
                    positionDate,
                    movementsShareAccount,
                    movementsShareAccountDescription,
                    movementsFund,
                    movementsFundDescription,
                    enableRangeDatefield ? movementsDates[0] : movementsDateFrom,
                    enableRangeDatefield ? movementsDates[1] : movementsDateTo,
                    requestsShareAccount,
                    requestsShareAccountDescription,
                    requestsDateFrom,
                    requestsDateTo,
                    verifyCode,
                    page,
                ),
            );
        },
    }),
)(FundcorpOperationsMain);
